import type { DateTime } from 'yo-services/google/type/datetime_pb';

import { getTimeZoneDateInUtc } from './i18n/getTimeZoneDateIn';

/**
 * Convert PostalAddress to Address. sycamore => front-end
 */
export const convertPostalAddressToAddress = (address?: PostalAddress): Address | null => {
  if (!address) {
    return null;
  }

  const street1 = address.addressLinesList.length > 0 ? address.addressLinesList[0] : '';
  const street2 = address.addressLinesList.length > 1 ? address.addressLinesList[1] : '';
  const postalCode = address.postalCode;
  const region = address.administrativeArea;
  const country = address.regionCode;
  const city = address.locality;

  return {
    street1: street1 || '',
    street2: street2 || '',
    postalCode,
    region,
    city,
    country,
  };
};

export const convertDateTimeProtoToTimestamp = (
  dateTime: DateTime.AsObject | undefined,
): number | null => {
  if (!dateTime) {
    return null;
  }

  const { timeZone, year, month, day, hours, minutes, seconds } = dateTime;

  if (!timeZone?.id) {
    return null;
  }

  const date = new Date();

  // For time-only values, year is set to 0, creates problems with Date()
  if (year) {
    date.setFullYear(year);
    date.setMonth(Math.max(month - 1, 0));
    date.setDate(Math.max(day, 1));
  }
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);
  date.setMilliseconds(0);

  const utcDate = getTimeZoneDateInUtc(date, timeZone.id);
  return utcDate?.getTime() ?? null;
};

/**
 * Convert google time proto to timestamp
 */
export const convertTimeProtoToTimestamp = (
  timestamp: GoogleTimeStamp | undefined,
): number | null => {
  if (!timestamp) {
    return null;
  }

  return new Date(timestamp?.seconds * 1000).getTime();
};

/**
 * Convert google money proto to amount
 */
export const convertMoneyProtoToAmount = (money: GoogleMoney | undefined): number | null => {
  if (!money) {
    return null;
  }

  return money.units + Math.pow(10, -9) * money.nanos;
};

export const convertTimeOfDayProtoToTime = (
  timeOfDay: TimeOfDay | undefined,
  formatter: Intl.DateTimeFormat,
): string => {
  if (!timeOfDay) {
    return '';
  }

  // The date doesn't matter, we only need the Date to parse out the localized time format using `Intl.DateTimeFormat`
  const time = new Date(2000, 1, 1, timeOfDay.hours, timeOfDay.minutes, 0);

  return formatter.format(time);
};
